import { useCallback, useEffect, useRef, useState } from 'react';

import type { ConsentFormValues } from '@/components/ApplePayExpressButton/schema';
import type {
  OnClickHandler,
  OnClickReject,
  OnClickResolve,
} from '@/components/ApplePayExpressButton/types';
import { useCheckout } from '@/hooks/useCheckout';
import { useUser } from '@/hooks/useUser';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';

interface UseApplePayTermsConsentProps {
  affiliateOptInRef: React.MutableRefObject<boolean>;
  areBraceletSizeErrorsSet: () => boolean;
}

export const useApplePayTermsConsent = ({
  affiliateOptInRef,
  areBraceletSizeErrorsSet,
}: UseApplePayTermsConsentProps) => {
  const {
    refresh,
    snapshot: { isIncludeNFLProduct },
  } = useCheckout<true>();
  const {
    snapshot: { isAuthenticated, user },
  } = useUser();
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const pendingResolversRef = useRef<{
    reject: OnClickReject;
    resolve: OnClickResolve;
  } | null>(null);
  const braceletSizeCheckRef = useRef<() => boolean>(() => true);
  const isUserAffiliated = user?.metadata?.some(
    (item) =>
      item.key === ACCOUNT_METADATA.isAffiliated &&
      item.value?.toLowerCase() === 'true',
  );
  const showNflMarketingOption: boolean =
    process.env.NEXT_PUBLIC_ENABLE_NFL_OPTIN_CHECKOUT === 'true' &&
    isIncludeNFLProduct &&
    !!isAuthenticated &&
    !isUserAffiliated;

  useEffect(() => {
    braceletSizeCheckRef.current = areBraceletSizeErrorsSet;
  }, [areBraceletSizeErrorsSet]);

  useEffect(() => {
    return () => {
      if (pendingResolversRef.current) {
        pendingResolversRef.current.reject?.(new Error('aborted'));
        pendingResolversRef.current = null;
      }
    };
  }, []);

  const handleClick: OnClickHandler = useCallback(
    (resolve, reject) => {
      if (!braceletSizeCheckRef.current()) {
        reject();
        return;
      }

      refresh(true).catch((err) => {
        console.warn('[ApplePay] Checkout refresh failed', err);
      });

      pendingResolversRef.current = { resolve, reject };
      setIsTermsOpen(true);
    },
    [setIsTermsOpen, refresh],
  );

  const onCancelConsent = useCallback(() => {
    pendingResolversRef.current?.reject(new Error('cancelled'));
    pendingResolversRef.current = null;
    setIsTermsOpen(false);
  }, []);

  const onConfirmConsent = useCallback((formData: ConsentFormValues) => {
    affiliateOptInRef.current = formData.userAffiliated;
    pendingResolversRef.current?.resolve(); // Trigger Apply Pay
    pendingResolversRef.current = null;
    setIsTermsOpen(false);
  }, []);

  return {
    isTermsOpen,
    showNflMarketingOption,
    onCancelConsent,
    onConfirmConsent,
    handleClick,
  };
};
